(() => {
  class AnnouncementCarousel extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = {signal: this.abort.signal};
      this.slides = [...this.querySelectorAll('[data-announcement-slide]')];
      this.index = 0;
      this.paused = this.dataset.autoplay !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.editing = false;
      const controls = this.querySelector('.announcement-controls');
      if (controls) controls.hidden = false;
      this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.show(this.index + 1), options);
      this.addEventListener('focusin', () => this.schedule(), options);
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.editing = true;
        this.show(index);
      }, options);
      document.addEventListener('shopify:block:deselect', () => { this.editing = false; this.schedule(); }, options);
      this.show(0);
    }
    disconnectedCallback() { clearTimeout(this.timer); this.abort?.abort(); }
    show(index) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== this.index;
        slide.classList.toggle('is-entering', i === this.index);
      });
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || this.slides.length < 2 || this.paused || this.editing || document.hidden || this.querySelector(':focus-visible')) return;
      this.timer = setTimeout(() => this.show(this.index + 1), Number(this.dataset.interval) || 5000);
    }
  }
  if (!customElements.get('announcement-carousel')) customElements.define('announcement-carousel', AnnouncementCarousel);

  let timer;
  let busy = false;
  const section = () => document.querySelector('[data-cart-section]');
  function note(message, error = false) {
    const element = section()?.querySelector('[data-cart-update-note]');
    if (!element) return;
    element.hidden = !message;
    element.classList.toggle('is-error', error);
    element.textContent = message;
  }
  function sync(root = document) {
    root.querySelectorAll('[data-quantity-control]').forEach(control => {
      control.querySelector('[data-quantity-step="-1"]').disabled = Number(control.querySelector('input').value) <= 0;
    });
  }
  function checkoutDisabled(disabled) {
    const checkout = section()?.querySelector('[name="checkout"]');
    if (checkout) checkout.disabled = disabled;
  }
  function lock(disabled) {
    const form = section()?.querySelector('[data-cart-form]');
    if (!form) return;
    form.setAttribute('aria-busy', String(disabled));
    form.querySelectorAll('input, button').forEach(control => { control.disabled = disabled; });
    if (!disabled) sync(form);
  }
  function render(html) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-section]');
    if (!next) throw new Error('Le panier n’a pas pu être affiché.');
    const current = section();
    const active = current?.contains(document.activeElement) ? document.activeElement : null;
    const line = active?.closest('.cart-item')?.querySelector('[data-cart-line]')?.dataset.cartLine;
    const step = active?.dataset.quantityStep;
    current.replaceWith(next);
    sync(next);
    if (active) {
      const input = next.querySelector(`[data-cart-line="${Number(line)}"]`);
      const target = step ? input?.closest('[data-quantity-control]').querySelector(`[data-quantity-step="${Number(step)}"]`) : input;
      if (target) target.focus({preventScroll: true});
      else { const title = next.querySelector('h1'); title.tabIndex = -1; title.focus({preventScroll: true}); }
    }
  }
  async function refresh(id) {
    const url = new URL(location.href);
    url.searchParams.set('section_id', id);
    const response = await fetch(url, {headers: {Accept: 'text/html'}, cache: 'no-store'});
    if (!response.ok) throw new Error('Impossible de relire le panier.');
    render(await response.text());
  }
  function updateCount(cart) {
    document.querySelectorAll('[data-cart-count]').forEach(count => { count.textContent = cart.item_count; });
  }
  async function flush() {
    clearTimeout(timer);
    if (busy || !section()) return;
    const changes = [...section().querySelectorAll('[data-cart-line]')]
      .filter(input => Number(input.value) !== Number(input.dataset.serverQuantity))
      .map(input => ({line: Number(input.dataset.cartLine), quantity: Number(input.value)}))
      // Descending lines keep the remaining line numbers valid after a removal.
      .sort((a, b) => b.line - a.line);
    if (!changes.length) { checkoutDisabled(false); note(''); return; }
    busy = true;
    const id = section().dataset.cartSection;
    const root = window.Shopify?.routes?.root || '/';
    lock(true);
    note('Mise à jour de votre panier…');
    let cart;
    let adjusted = false;
    try {
      for (const change of changes) {
        const response = await fetch(root + 'cart/change.js', {
          method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({...change, sections: [id], sections_url: location.pathname})
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(typeof data.description === 'string' ? data.description : 'Cette quantité n’est pas disponible.');
        cart = data;
        updateCount(cart);
        if (change.quantity > 0 && cart.items[change.line - 1]?.quantity !== change.quantity) adjusted = true;
      }
      if (cart.sections?.[id]) render(cart.sections[id]);
      else await refresh(id);
      note(adjusted ? 'La quantité a été ajustée selon la disponibilité. Votre total est à jour.' : 'Votre panier est à jour.');
    } catch (error) {
      let recovered = false;
      try {
        await refresh(id);
        const response = await fetch(root + 'cart.js', {cache: 'no-store'});
        if (response.ok) updateCount(await response.json());
        recovered = true;
      } catch (_) { /* Keep checkout blocked until the authoritative cart is available. */ }
      note(recovered ? error.message : 'La connexion a été interrompue. Rechargez le panier pour vérifier votre total.', true);
      if (!recovered) {
        const link = document.createElement('a');
        link.href = location.pathname;
        link.textContent = ' Recharger le panier';
        section().querySelector('[data-cart-update-note]').append(link);
        busy = false;
        return;
      }
    }
    busy = false;
    lock(false);
  }
  function schedule(input) {
    if (busy) return;
    if (!input.checkValidity() || input.value === '') {
      input.value = input.dataset.serverQuantity;
      note('Choisissez une quantité entière, à partir de 0.', true);
      sync();
      const pending = [...section().querySelectorAll('[data-cart-line]')].some(field => Number(field.value) !== Number(field.dataset.serverQuantity));
      checkoutDisabled(pending);
      if (pending) { clearTimeout(timer); timer = setTimeout(flush, 300); }
      return;
    }
    sync();
    checkoutDisabled(true);
    note('Mise à jour de votre panier…');
    clearTimeout(timer);
    timer = setTimeout(flush, 300);
  }
  document.addEventListener('click', event => {
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      if (busy) return;
      const input = section().querySelector(`[data-cart-line="${Number(remove.dataset.cartRemove)}"]`);
      input.value = '0';
      schedule(input);
    }
    const step = event.target.closest('[data-quantity-step]');
    if (step && !busy) {
      const input = step.closest('[data-quantity-control]').querySelector('input');
      input.value = String(Math.max(0, Number(input.value || 0) + Number(step.dataset.quantityStep)));
      schedule(input);
    }
  });
  document.addEventListener('input', event => {
    if (event.target.matches('[data-cart-line]')) {
      // Wait briefly for a complete typed quantity before recalculating.
      checkoutDisabled(true);
      clearTimeout(timer);
      timer = setTimeout(() => schedule(event.target), 500);
    }
  });
  document.addEventListener('change', event => {
    if (event.target.matches('[data-cart-line]')) schedule(event.target);
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-cart-line]')) {
      event.preventDefault(); schedule(event.target);
    }
  });
  document.addEventListener('submit', event => {
    if (event.target.matches('[data-cart-form]') && (busy || [...event.target.querySelectorAll('[data-cart-line]')].some(input => input.value !== input.dataset.serverQuantity))) {
      event.preventDefault();
      if (!busy) flush();
    }
  });
  document.addEventListener('shopify:section:load', () => sync());
  sync();
})();
